<script setup lang="ts">
/**
 * "…" in the chat header (owner 17 ต.ค. 69, lean panel: what does not help answer the customer right now leaves the
 * side panel — moved, not deleted):
 *   (แท็กลูกค้า moved to its own header button "ติดแท็ก" — ChatTagButton.vue, owner 18 ต.ค. 69 "ใช้บ่อย")
 *   เพิ่มโน้ตภายใน        the typing box switches to "โน้ตภายใน" (CollabBar, Alt+N)
 *   บล็อกผู้ส่งนี้        web chats the person may block now (server `can.block`, not blocked yet) → WebBlockPanel dialog
 *   ล้างแชทนี้ (ทดสอบ)    owner/head on a test-account chat (server `dev_resettable`) → DevResetButton dialog
 * A menu button (aria-haspopup="menu"): Enter/Space/↓ opens and focuses the first item, ↑/↓/Home/End move, Escape or
 * Tab closes (focus back on the button), a click outside closes.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import type { ConversationDetail } from '../../api/types'
import { useCollab } from '../../stores/collab'
import WebBlockPanel from '../side/WebBlockPanel.vue'
import Icon from '../ui/Icon.vue'
import type { IconName } from '../ui/icons'
import DevResetButton from './DevResetButton.vue'

const props = defineProps<{ detail: ConversationDetail }>()
const collab = useCollab()

const open = ref(false)
const wrap = ref<HTMLElement | null>(null)
const btn = ref<HTMLButtonElement | null>(null)
const menu = ref<HTMLElement | null>(null)
const block = ref<InstanceType<typeof WebBlockPanel> | null>(null)
const dev = ref<InstanceType<typeof DevResetButton> | null>(null)

const web = computed(() => props.detail.channel === 'web' && props.detail.kind === 'dm')

interface Item {
  id: 'note' | 'block' | 'reset'
  label: string
  sub: string | null
  icon: IconName
  danger?: boolean
}
const items = computed<Item[]>(() => {
  const out: Item[] = []
  out.push({ id: 'note', label: 'เพิ่มโน้ตภายใน', sub: 'เห็นเฉพาะพนักงาน', icon: 'note' })
  if (web.value && block.value?.canBlock) out.push({ id: 'block', label: 'บล็อกผู้ส่งนี้', sub: block.value.hitsLine ?? null, icon: 'shield' })
  if (props.detail.dev_resettable) out.push({ id: 'reset', label: 'ล้างแชทนี้ (ทดสอบ)', sub: 'บัญชีทดสอบ · เริ่มคุยใหม่ตั้งแต่ต้น', icon: 'refresh', danger: true })
  return out
})

function buttons(): HTMLButtonElement[] {
  return [...(menu.value?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])]
}
function focusAt(i: number): void {
  const list = buttons()
  if (!list.length) return
  list[(i + list.length) % list.length]?.focus()
}

async function show(focusIndex = 0): Promise<void> {
  open.value = true
  await nextTick()
  focusAt(focusIndex)
}
function hide(refocus = true): void {
  open.value = false
  if (refocus) btn.value?.focus()
}
function toggle(): void {
  if (open.value) hide()
  else void show()
}

function onButtonKey(e: KeyboardEvent): void {
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    void show(e.key === 'ArrowUp' ? -1 : 0)
  }
}

function onMenuKey(e: KeyboardEvent): void {
  const list = buttons()
  const i = list.indexOf(document.activeElement as HTMLButtonElement)
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    focusAt(i + 1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    focusAt(i - 1)
  } else if (e.key === 'Home') {
    e.preventDefault()
    focusAt(0)
  } else if (e.key === 'End') {
    e.preventDefault()
    focusAt(-1)
  } else if (e.key === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    hide()
  } else if (e.key === 'Tab') {
    hide(false)
  }
}

function onDoc(e: MouseEvent): void {
  if (!wrap.value?.contains(e.target as Node)) hide(false)
}
watch(open, (v) => {
  if (v) document.addEventListener('mousedown', onDoc)
  else document.removeEventListener('mousedown', onDoc)
})
watch(
  () => props.detail.id,
  () => {
    open.value = false
  },
)
onBeforeUnmount(() => document.removeEventListener('mousedown', onDoc))

function run(id: Item['id']): void {
  hide(false)
  if (id === 'note') collab.setNoteMode(props.detail.id, true)
  else if (id === 'block') block.value?.showBlock()
  else if (id === 'reset') dev.value?.show()
}
</script>

<template>
  <div ref="wrap" class="more">
    <button
      ref="btn"
      type="button"
      class="btn ico more-b"
      :class="{ on: open }"
      aria-haspopup="menu"
      :aria-expanded="open"
      aria-controls="chat-more-menu"
      aria-label="เมนูแชทเพิ่มเติม: โน้ต บล็อก ล้างแชท"
      title="เมนูแชทเพิ่มเติม"
      @click="toggle"
      @keydown="onButtonKey"
    >
      <Icon name="more" />
    </button>
    <ul v-if="open" id="chat-more-menu" ref="menu" class="more-pop" role="menu" aria-label="เมนูแชท" @keydown="onMenuKey">
      <li v-for="it in items" :key="it.id" role="none" :class="{ sep: it.danger }">
        <button type="button" role="menuitem" tabindex="-1" class="more-i" :class="{ danger: it.danger }" @click="run(it.id)">
          <Icon :name="it.icon" />
          <span class="more-t">
            <span class="more-l">{{ it.label }}</span>
            <span v-if="it.sub" class="more-s">{{ it.sub }}</span>
          </span>
        </button>
      </li>
    </ul>
    <WebBlockPanel v-if="web" ref="block" :conv-id="detail.id" mode="menu" :name="detail.contact.name" />
    <DevResetButton v-if="detail.dev_resettable" ref="dev" :conv-id="detail.id" :name="detail.contact.name" :channel="detail.channel" headless />
  </div>
</template>

<style scoped>
.more {
  position: relative;
}
.more-b :deep(.ic) {
  width: 18px;
  height: 18px;
  color: var(--ink);
}
.more-b.on {
  background: #eef2f0;
}
.more-pop {
  position: absolute;
  right: 0;
  top: 38px;
  z-index: 21;
  min-width: 260px;
  max-width: 320px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(23, 33, 28, 0.12);
}
.more-pop li.sep {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid var(--line2);
}
.more-i {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 7px;
  text-align: left;
}
.more-i:hover,
.more-i:focus-visible {
  background: #f4f6f5;
  outline: none;
}
.more-i:focus-visible {
  box-shadow: inset 0 0 0 2px #9fcdb8;
}
.more-i > :deep(.ic) {
  flex: none;
  width: 17px;
  height: 17px;
  margin-top: 2px;
  color: var(--mut);
}
.more-t {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.more-l {
  font-size: 13.5px;
  font-weight: 500;
  line-height: 21px;
}
.more-s {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.more-i.danger .more-l,
.more-i.danger > :deep(.ic) {
  color: var(--crit);
}
</style>
